import React, { useMemo, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator } from 'react-native-web';
import CampaignPreview from './CampaignPreview';

// Individual campaign item wrapper
const CampaignItem = React.memo(({ campaign, openArchiveModal }) => {
  return (
    <View style={styles.campaignItemContainer}>
      <CampaignPreview 
        campaign={campaign} 
        alternateBackground={true}
        openArchiveModal={openArchiveModal}
      />
    </View>
  );
});

// Empty state component
const EmptyCampaigns = React.memo(() => (
  <View style={styles.emptyContainer}>
    <Text style={styles.emptyIcon}>📋</Text>
    <Text style={styles.emptyTitle}>No active campaigns yet</Text>
    <Text style={styles.emptyDescription}>
      Create your first campaign to start collaborating with creators!
    </Text>
  </View>
));

// Loading skeleton component
const CampaignsSkeleton = React.memo(() => (
  <View style={styles.skeletonContainer}>
    {[1, 2, 3].map((index) => (
      <View key={index} style={styles.skeletonItem}>
        <View style={styles.skeletonHeader}>
          <View style={styles.skeletonImage} />
          <View style={styles.skeletonContent}>
            <View style={styles.skeletonTextLong} />
            <View style={styles.skeletonTextMedium} />
            <View style={styles.skeletonTextShort} />
          </View>
        </View>
        <View style={styles.skeletonFooter}>
          <View style={styles.skeletonButton} />
          <View style={styles.skeletonStats}>
            <View style={styles.skeletonStat} />
            <View style={styles.skeletonStat} />
          </View>
        </View>
      </View>
    ))}
  </View>
));

const CampaignHub = React.memo(({ campaigns, isLoading, onRefresh, openArchiveModal }) => {
  // Memoized campaign processing
  const processedCampaigns = useMemo(() => {
    if (!campaigns || !Array.isArray(campaigns)) return [];
    
    // Sort campaigns by most recent first, then by status priority
    return [...campaigns].sort((a, b) => {
      // Priority order: active > pending > completed
      const statusPriority = { active: 3, pending: 2, completed: 1 };
      const aPriority = statusPriority[a.status?.toLowerCase()] || 0;
      const bPriority = statusPriority[b.status?.toLowerCase()] || 0;
      
      if (aPriority !== bPriority) {
        return bPriority - aPriority;
      }
      
      // If same status, sort by creation date (most recent first)
      const aDate = new Date(a.created_at || a.createdAt || 0);
      const bDate = new Date(b.created_at || b.createdAt || 0);
      return bDate - aDate;
    });
  }, [campaigns]);

  // Memoized campaign statistics
  const campaignStats = useMemo(() => {
    if (!processedCampaigns.length) return null;
    
    const stats = processedCampaigns.reduce((acc, campaign) => {
      const status = campaign.status?.toLowerCase() || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      acc.total = (acc.total || 0) + 1;
      return acc;
    }, {});
    
    return stats;
  }, [processedCampaigns]);

  const handleRefresh = useCallback(() => {
    if (onRefresh && typeof onRefresh === 'function') {
      onRefresh();
    }
  }, [onRefresh]);

  if (isLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>Active Campaigns</Text>
        </View>
        <CampaignsSkeleton />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.titleContainer}>
          <Text style={styles.title}>Active Campaigns</Text>
          {campaignStats && (
            <View style={styles.statsContainer}>
              <Text style={styles.statsText}>
                {campaignStats.total} campaign{campaignStats.total !== 1 ? 's' : ''}
              </Text>
              {campaignStats.active > 0 && (
                <View style={styles.activeBadge}>
                  <Text style={styles.activeBadgeText}>
                    {campaignStats.active} active
                  </Text>
                </View>
              )}
            </View>
          )}
        </View>
      </View>

      <View style={styles.campaignContainer}>
        {(!processedCampaigns || processedCampaigns.length === 0) ? (
          <EmptyCampaigns />
        ) : (
          <ScrollView 
            style={styles.scrollView}
            showsVerticalScrollIndicator={true}
            contentContainerStyle={styles.scrollContent}
          >
            {processedCampaigns.map((campaign) => (
              <CampaignItem
                key={campaign.id}
                campaign={campaign}
                openArchiveModal={openArchiveModal}
              />
            ))}
          </ScrollView>
        )}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0, // Allows container to be constrained by parent
    maxHeight: '580px', // Prevents overflow beyond parent
  },
  header: {
    marginBottom: 16,
  },
  titleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#396de0',
    fontFamily: "'forma-djr-display', sans-serif",
  },
  statsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  statsText: {
    fontSize: 14,
    color: '#6b7280',
    fontFamily: "'lato', sans-serif",
    fontWeight: '500',
  },
  activeBadge: {
    backgroundColor: '#dcfce7',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#16a34a',
  },
  activeBadgeText: {
    fontSize: 12,
    color: '#16a34a',
    fontFamily: "'lato', sans-serif",
    fontWeight: '600',
  },
  campaignContainer: {
    flex: 1,
    minHeight: 0, // Ensures flex child can shrink below content size
  },
  scrollView: {
    flex: 1,
    minHeight: 0, // Critical for proper flex scrolling
  },
  scrollContent: {
    paddingBottom: 10,
  },
  campaignItemContainer: {
    marginBottom: 12,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
    paddingHorizontal: 40,
    minHeight: 150,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: 16,
    opacity: 0.5,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#374151',
    fontFamily: "'forma-djr-display', sans-serif",
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyDescription: {
    fontSize: 14,
    color: '#6b7280',
    fontFamily: "'lato', sans-serif",
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 280,
  },
  skeletonContainer: {
    flex: 1,
  },
  skeletonItem: {
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  skeletonHeader: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  skeletonImage: {
    width: 60,
    height: 60,
    borderRadius: 8,
    backgroundColor: '#e5e7eb',
    marginRight: 12,
  },
  skeletonContent: {
    flex: 1,
    justifyContent: 'space-between',
  },
  skeletonTextLong: {
    height: 16,
    backgroundColor: '#e5e7eb',
    borderRadius: 4,
    width: '85%',
    marginBottom: 6,
  },
  skeletonTextMedium: {
    height: 14,
    backgroundColor: '#e5e7eb',
    borderRadius: 4,
    width: '70%',
    marginBottom: 6,
  },
  skeletonTextShort: {
    height: 12,
    backgroundColor: '#e5e7eb',
    borderRadius: 4,
    width: '50%',
  },
  skeletonFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  skeletonButton: {
    height: 32,
    width: 80,
    backgroundColor: '#e5e7eb',
    borderRadius: 16,
  },
  skeletonStats: {
    flexDirection: 'row',
    gap: 8,
  },
  skeletonStat: {
    height: 20,
    width: 40,
    backgroundColor: '#e5e7eb',
    borderRadius: 4,
  },
});

CampaignItem.displayName = 'CampaignItem';
EmptyCampaigns.displayName = 'EmptyCampaigns';
CampaignsSkeleton.displayName = 'CampaignsSkeleton';
CampaignHub.displayName = 'CampaignHub';

export default CampaignHub; 